import { FormattedMessage } from 'react-intl';
import {
    BulkStatusUpdateErrorCode,
    getActiveCompoundManagementView,
    getBulkStatusUpdateErrorCode,
    getLoadPreview,
} from '../../../reducers/compoundManagementSlice';
import { useAppSelector } from '../../../../../configuration/setup/hooks';
import { BulkUpdateMaxSizeErrorMessage } from '../../../sharedComponents/bulkStatusUpdate/BulkUpdateMaxSizeErrorMessage';
import { MAX_BULK_SIZE } from './BulkStatusUpdateDialog';
import { useLoadPreviewSelection } from '../toolbar/StartBulkStatusUpdateButton';
import Rioglyph from '@rio-cloud/rio-uikit/Rioglyph';
import { FunctionComponent } from 'react';
import {
    BulkStatusError,
    BulkStatusErrorMessage,
} from '../../../sharedComponents/bulkStatusUpdate/BulkStatusErrorMessage';
import { LoadPreviewError, LoadPreviewErrorCode } from '../../compoundManagement.types';

export const BulkStatusUpdateValidationFeedback = () => {
    const loadPreview = useAppSelector(getLoadPreview);
    const errorCode = useAppSelector(getBulkStatusUpdateErrorCode);
    const currentCompoundView = useAppSelector(getActiveCompoundManagementView);

    const { getCurrentSelectedVehicleIds } = useLoadPreviewSelection(currentCompoundView);

    const invalidVehicles = loadPreview?.invalidVins ?? [];
    const validVehiclesInLoad = loadPreview?.vehicles.length ?? 0;
    const selectedVehicleIdsCount = getCurrentSelectedVehicleIds()?.length ?? 0;
    const exceedsMaxBulkSelection = selectedVehicleIdsCount > MAX_BULK_SIZE;

    const title = (
        <div className="text-size-16">
            <FormattedMessage id="outboundOrderBook.compoundManagement.bulkStatusUpdate.validationFeedback.title" />
        </div>
    );

    const feedback =
        errorCode === BulkStatusUpdateErrorCode.DUPLICATE_VIN ? (
            <ErrorFeedback messageId="outboundOrderBook.compoundManagement.bulkStatusUpdate.validationFeedback.failure.duplicateVin" />
        ) : exceedsMaxBulkSelection || errorCode === BulkStatusUpdateErrorCode.SIZE_EXCEEDED ? (
            <BulkUpdateMaxSizeErrorMessage
                identifierType="VEHICLE"
                totalCount={selectedVehicleIdsCount}
                maxBulkSize={MAX_BULK_SIZE}
            />
        ) : validVehiclesInLoad === 0 ? (
            <ErrorFeedback messageId="outboundOrderBook.compoundManagement.bulkStatusUpdate.validationFeedback.failure" />
        ) : (
            <SuccessFeedback />
        );

    return (
        <div className="min-height-300 display-flex flex-column justify-content-center align-items-center gap-20">
            {title}
            {feedback}
            {invalidVehicles.length > 0 && <BulkStatusErrorMessage errors={mapVehicleErrors(invalidVehicles)} />}
        </div>
    );
};

const mapVehicleErrors = (invalidVehicles: LoadPreviewError[]): BulkStatusError<LoadPreviewErrorCode>[] =>
    invalidVehicles.map((error) => ({
        errorCode: error.errorCode,
        identifiers: error.vins,
        identifierType: 'VEHICLE' as const,
    }));

const ErrorFeedback: FunctionComponent<{ messageId: string }> = ({ messageId }) => (
    <>
        <div>
            <Rioglyph icon="rioglyph-remove-sign" iconClassName="text-color-danger" size="h1" />
        </div>
        <div>
            <FormattedMessage id={messageId} />
        </div>
    </>
);

const SuccessFeedback: FunctionComponent = () => {
    const loadPreview = useAppSelector(getLoadPreview);

    const invalidVehicles = loadPreview?.invalidVins ?? [];
    const uniqueInvalidVins = new Set(invalidVehicles.flatMap((error) => error.vins)).size;
    const validVehiclesInLoad = loadPreview?.vehicles.length ?? 0;
    const allVehiclesInLoad = validVehiclesInLoad + uniqueInvalidVins;

    return (
        <>
            <div>
                <Rioglyph icon="rioglyph-check-badge" iconClassName="text-color-success" size="h1" />
            </div>
            <div>
                <FormattedMessage
                    id="outboundOrderBook.compoundManagement.bulkStatusUpdate.validationFeedback.successCount"
                    values={{ successCount: validVehiclesInLoad, totalCount: allVehiclesInLoad }}
                />
            </div>
        </>
    );
};
